import type { RefObject } from 'react'
import { useNow } from '../../../hooks/useNow'
import { useNowLineDrag } from './useNowLineDrag'
import { NowIndicator } from './NowIndicator'
import { useGridGeometry } from './useGridGeometry'
import { computeNowLineMinutes } from './timeUtils'
import { useDayCutoff } from '../../../contexts/DayCutoffContext'

// The clock-driven now-line, isolated in its own component so the per-minute
// tick (and the 200ms festival-preview simulation tick) re-renders ONLY this
// subtree — not the whole DayGrid and its 50–100 SetBlocks. The grid owns the
// scrolling element and passes its ref down for the drag-to-retime math.
export function NowLine({
  gridRef,
  gridMin,
  gridMax,
  day,
  timezone,
}: {
  gridRef: RefObject<HTMLDivElement | null>
  gridMin: number
  gridMax: number
  day: string
  timezone?: string
}) {
  const cutoff = useDayCutoff()
  // Reactive "now": re-renders on the per-minute tick AND immediately when the
  // clock override changes (including a now-line drag commit), so the line
  // settles at the dropped position with no remount.
  const now = useNow(60_000)
  const geometry = useGridGeometry(gridRef)
  const nowLineMin = computeNowLineMinutes(now, day, gridMin, gridMax, gridMax > gridMin, cutoff, timezone)
  const { indicator } = useNowLineDrag({
    now,
    nowLineMin,
    gridMin,
    gridMax,
    day,
    timezone,
    gridRef,
    geometry,
  })
  if (!indicator) return null
  return <NowIndicator {...indicator} />
}
